import DateFnsUtils from "@date-io/date-fns";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import DeleteIcon from "@material-ui/icons/Delete";
import ScheduleIcon from "@material-ui/icons/Schedule";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import ClassNames from "classnames";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import Accordion from "../../components/Accordion";
import BackButton from "../../components/BackButton";
import Button from "../../components/Button";
import GentleReminderCard from "../../components/GentleReminderCard";
import NotesSidebar from "../../components/NotesSidebar";
import PageContent from "../../components/PageContent";
import Section from "../../components/Section";
import SectionTitle from "../../components/SectionTitle";
import SessionSummary from "../../components/SessionSummary";
import Stepper from "../../components/Stepper";
import { IsSessionCoach, IsSessionCoachee } from "../../config/Roles";
import { useNotification } from "../../context/notification";
import { DashboardLayout } from "../../layouts/DashboardLayout";
import { apiRequest } from "../../utils/api";
import { getLocalValue } from "../../utils/auth";
import { dayJS, utcToLocal } from "../../utils/datetime";

import { useTranslation } from "react-i18next";
import SessionBasicInfo from "../../components/SessionBasicInfo";
import GetSessionData from "../../services/getSessionData";
import styles from "./SessionsSetCheckpoints.module.scss";

const checkpointTemplate = {
  checkpoint_date: null,
  disabled: false,
  checkpoint_progress_value: "",
  new: true,
};
export function SessionsEditCheckpoints() {
  const history = useHistory();
  const { addNotification } = useNotification();
  const { id } = useParams();
  const { t } = useTranslation();
  const [rating, setRating] = useState(0);
  const [sessionDetails, setSessionDetails] = useState({});
  const [growFormDetails, setGrowFormDetails] = useState({});
  const [checkpoints, setCheckpoints] = useState([]);
  const [deletedCheckpoints, setDeletedCheckpoints] = useState([]);
  const [checkpointCopy, setCheckpointCopy] = useState([]);
  const [sessionCoach, setSessionCoach] = useState(null);
  const [sessionCoachee, setSessionCoachee] = useState(null);
  const [sessionDuration, setSessionDuration] = useState(0);
  const [checkpointFull, setCheckpointFull] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const initialForm = {
    checkpoints: [],
  };
  if (!localStorage.checkPointEditData) {
    localStorage.setItem("checkPointEditData", JSON.stringify(initialForm));
  }

  useEffect(() => {
    setTimeout(() => {
      if (localStorage.checkPointEditData) {
        const tmp = JSON.parse(localStorage.checkPointEditData);
        if (tmp.checkpoints && tmp.checkpoints.length > 0) {
          setCheckpoints(tmp.checkpoints);
        }
      }
    }, 2000);
  }, []);

  useEffect(() => {
    // get session details
    async function fetchSessionData() {
      setIsLoading(true);
      const sessionAPIData = await GetSessionData(id);
      if (sessionAPIData) {
        setSessionDetails(sessionAPIData);
      } else {
        addNotification(
          `Could not get session details from id #${id}`,
          "error"
        );
      }
      setIsLoading(false);
    }

    fetchSessionData();

    // get checkpoint details
    apiRequest
      .get(`/api/getCheckpointDetails?session_id=${id}`)
      .then(response => {
        const data = response.data.data;

        if (data && data.progress_data) {
          const filtered = data.progress_data.filter(item => {
            return item.subject_id === data.progress_data[0].subject_id;
          });

          // sort date ascendingly
          filtered.sort((a, b) => {
            let aTime = new Date(a.checkpoint_date);
            let bTime = new Date(b.checkpoint_date);
            aTime = aTime.getTime();
            bTime = bTime.getTime();

            return aTime - bTime;
          });
          setCheckpoints(filtered);
          setCheckpointCopy(filtered);
          if (localStorage.checkPointEditData) {
            const tmp = JSON.parse(localStorage.checkPointEditData);
            tmp.checkpoints = filtered;
            localStorage.setItem(`checkPointEditData`, JSON.stringify(tmp));
          }
        }
      })
      .catch(error => {
        console.error(error);
      });
  }, []);

  useEffect(() => {
    setSessionCoach(IsSessionCoach(getSessionValue("coach_id")));
    setSessionCoachee(IsSessionCoachee(getSessionValue("coachee_id")));
  }, [sessionDetails]);

  const handleCheckpointAdd = () => {
    if (checkpoints.length >= 5) {
      addNotification("You can only add a maximum of 4 checkpoints", "warning");
    } else {
      let newCheckpoints = _.cloneDeep(checkpoints);
      let newCP = checkpointTemplate;

      // making sure newly-added checkpoint's initial date is greater than its previous checkpoints' dates
      if (checkpoints.length >= 1) {
        newCP.checkpoint_date =
          checkpoints[checkpoints.length - 1].checkpoint_date;
        newCP.checkpoint_date = dayJS(newCP.checkpoint_date)
          .add(1, "day")
          .add(dayJS().utcOffset()) // convertfrom UTC to local timezone
          .format("YYYY-MM-DD 00:00:00");
      }
      newCheckpoints.push(newCP);
      setCheckpoints(newCheckpoints);

      if (localStorage.checkPointEditData) {
        const tmp = JSON.parse(localStorage.checkPointEditData);
        tmp.checkpoints = newCheckpoints;
        localStorage.setItem(`checkPointEditData`, JSON.stringify(tmp));
      }
    }
  };

  const handleCheckpointDelete = index => {
    let newCheckpoints = _.cloneDeep(checkpoints);

    setDeletedCheckpoints([...deletedCheckpoints, newCheckpoints[index]]);
    newCheckpoints.splice(index, 1);
    setCheckpoints(newCheckpoints);
    if (localStorage.checkPointEditData) {
      const tmp = JSON.parse(localStorage.checkPointEditData);
      tmp.checkpoints = newCheckpoints;
      localStorage.setItem(`checkPointEditData`, JSON.stringify(tmp));
    }
  };

  const handleDateChange = (date, index) => {
    let newCheckpoints = _.cloneDeep(checkpoints);
    newCheckpoints[index].checkpoint_date = dayJS(date)
      .add(dayJS().utcOffset(), "minutes") // convertfrom UTC to local timezone
      .format("YYYY-MM-DD 00:00:00");

    setCheckpoints(newCheckpoints);
    if (localStorage.checkPointEditData) {
      const tmp = JSON.parse(localStorage.checkPointEditData);
      tmp.checkpoints = newCheckpoints;
      localStorage.setItem(`checkPointEditData`, JSON.stringify(tmp));
    }
  };

  // helper function to access values within session details
  const getSessionValue = key => {
    return sessionDetails.session ? sessionDetails.session[key] : undefined;
  };

  // get current session duration for the stepper
  const getTotalTime = time => {
    setSessionDuration(time);
  };

  // check if the last checkpoint item is already 1 day before to review date (the last valid date). if so, disable add checkpoint button.
  useEffect(() => {
    if (getSessionValue("review_datetime")) {
      let lastCheckpoint = new Date(
        dayJS(checkpoints[checkpoints.length - 1].checkpoint_date)
          .add(1, "day")
          .format("YYYY-MM-DD")
      );
      let reviewDate = new Date(
        dayJS(getSessionValue("review_datetime"))
          .add(dayJS().utcOffset(), "minutes") // convertfrom UTC to local timezone
          .format("YYYY-MM-DD")
      );

      if (lastCheckpoint >= reviewDate) {
        setCheckpointFull(true);
      } else {
        setCheckpointFull(false);
      }
    }
  }, [checkpoints]);

  const isEditable = item => {
    const checkPointDate = new Date(
      utcToLocal(item.checkpoint_date).format("DD MMM YYYY")
    ).setHours(0, 0, 0, 0);
    const today = new Date().setHours(0, 0, 0, 0);
    if (
      checkPointDate >= today &&
      (item.checkpoint_progress_value === "" ||
        _.isNull(item.checkpoint_progress_value))
    ) {
      return true;
    }

    return false;
  };

  const handleConfirm = () => {
    const temp = JSON.parse(localStorage.checkPointEditData);
    const paramsObj = {
      session_id: parseInt(id),
      created_by: getSessionValue("session_created_by"),
      checkpoint_dates: temp.checkpoints.map(checkpoint => {
        return checkpoint.checkpoint_date;
      }),
      duration: sessionDuration,
    };

    setIsLoading(true);
    apiRequest
      .post(`/api/editCheckpointDetails`, paramsObj)
      .then(response => {
        if (response && response.status && response.status === 200) {
          localStorage.removeItem("checkPointEditData");
          history.push(`/sessions/update-checkpoints/${id}`); // goto next page in flow
        }
        setIsLoading(false);
      })
      .catch(error => {
        setIsLoading(false);
        console.error(error);
      });
  };

  return (
    <DashboardLayout
      appBarContent={
        sessionDetails && (
          <Stepper
            phase={t("header.stepper.check")}
            phaseLabel="Check"
            sessionId={id}
            clockRun={true}
            getTotalTimeSpent={getTotalTime}
          />
        )
      }
      rightSidebarContent={
        <NotesSidebar
          sessionID={id}
          isCoach={
            sessionDetails["coach_id"] === parseInt(getLocalValue("uid"))
          }
          accessible={true}
        />
      }
      contentClass={styles.dashboardLayout__content}
    >
      <div className={styles.pageWrapper}>
        <PageContent indent>
          <Section>
            <BackButton text={t("common.backToSession")} />
            <SectionTitle>Edit checkpoints</SectionTitle>

            <div className={styles.detailsContainer}>
              <div className={styles.sessionDetailsBlock}>
                <SessionBasicInfo sessionData={sessionDetails.session} />
                <div>
                  <Accordion
                    summary={t("sessionSummary.viewSessionSummary")}
                    details={<SessionSummary id={id}></SessionSummary>}
                  />
                </div>
              </div>
            </div>
          </Section>
          {
            <Section>
              <div
                style={{ marginTop: "35px" }}
                className={styles.sessionCheckpointsBlock}
              >
                <div className={styles.editLabel}>
                  You may only change the upcoming date.
                </div>
                {checkpoints && (
                  <div className={styles.checkpointContainer}>
                    {checkpoints.map((item, index) => (
                      <div
                        className={ClassNames(
                          styles.checkpointsRow,
                          styles.row
                        )}
                        key={index}
                      >
                        <div
                          className={ClassNames(
                            styles.rowLeft,
                            styles.checkpointsLabel
                          )}
                        >
                          {t("updateCheckpoint.checkpoint")} {index + 1}
                        </div>
                        <div
                          className={styles.rowRight}
                          style={{ alignItems: "flex-start" }}
                        >
                          {!isEditable(item) && (
                            <div style={{ padding: "10px 0" }}>
                              <span className={styles.rowIcon}>
                                <CalendarTodayIcon />
                              </span>
                              <span style={{ padding: "0 12px 0 4px" }}>
                                {utcToLocal(item.checkpoint_date).format(
                                  "DD MMM YYYY"
                                )}
                              </span>
                            </div>
                          )}
                          {isEditable(item) && (
                            <>
                              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                <CalendarTodayIcon
                                  className={styles.pickerIcon}
                                  fontSize="inherit"
                                  style={{ top: "15px" }}
                                />
                                <DatePicker
                                  disableToolbar
                                  variant="inline"
                                  inputVariant="outlined"
                                  disablePast={true}
                                  defaultValue={item.checkpoint_date}
                                  minDate={
                                    index === 0
                                      ? dayJS(
                                        getSessionValue("session_datetime")
                                      )
                                        .add(dayJS().utcOffset(), "minutes") // convert from UTC To local timezone
                                        .add(1, "day") // first checkpoint must be at least 1 day after coaching date
                                      : dayJS(
                                        checkpoints[index - 1].checkpoint_date
                                      ).add(1, "day") // no duplicate dates. cannot pick same day as the checkpoint item before.
                                  }
                                  autoOk
                                  maxDate={
                                    checkpoints.length > index + 1
                                      ? dayJS(
                                        checkpoints[index + 1].checkpoint_date
                                      ).subtract(1, "day") // no duplicate dates. cannot exceed the next checkpoint item's date
                                      : dayJS(
                                        getSessionValue("review_datetime")
                                      )
                                        .add(dayJS().utcOffset(), "minutes") // convert from UTC To local timezone
                                        .subtract(1, "day")
                                  }
                                  className={styles.datePicker}
                                  format="dd/M/yyyy"
                                  onChange={e => handleDateChange(e, index)}
                                  value={item.checkpoint_date}
                                  animateYearScrolling
                                />
                              </MuiPickersUtilsProvider>
                              <div className={styles.actions}>
                                {checkpoints.length > 1 && (
                                  <DeleteIcon
                                    onClick={() =>
                                      handleCheckpointDelete(index)
                                    }
                                  />
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                    <div
                      className={ClassNames(styles.checkpointsRow, styles.row)}
                    >
                      <div className={styles.rowLeft}></div>
                      <div className={styles.rowRight}>
                        <Button
                          className={styles.checkpointsAddBtn}
                          variant="outlined"
                          disabled={checkpoints.length >= 5 || checkpointFull}
                          onClick={handleCheckpointAdd}
                        >
                          {t("setCheckpoint.addCheckpoint")}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                {getSessionValue("review_datetime") ? (
                  <div className={styles.row}>
                    <div className={styles.rowLeft}>
                      {t("setCheckpoint.reviewDate")}
                    </div>
                    <div className={styles.rowRight}>
                      <span className={styles.rowIcon}>
                        <CalendarTodayIcon />
                      </span>
                      <span style={{ padding: "0 12px 0 4px" }}>
                        {/* Time format: 9 Jan 2020 */}
                        {dayJS(getSessionValue("review_datetime"))
                          .add(dayJS().utcOffset(), "minutes") // convert from UTC To local timezone
                          .format("D MMM YYYY")}
                      </span>
                      <span className={styles.rowIcon}>
                        <ScheduleIcon />
                      </span>
                      <span style={{ padding: "0 12px 0 4px" }}>
                        {/* Time format: 8:12 PM*/}
                        {dayJS(getSessionValue("review_datetime"))
                          .add(dayJS().utcOffset(), "minutes") // convert from UTC To local timezone
                          .format("h:mm A")}
                      </span>
                    </div>
                  </div>
                ) : (
                  ""
                )}
              </div>
            </Section>
          }
        </PageContent>
        {sessionCoach && (
          <PageContent indent>
            <Section>
              <div className={styles.detailsContainer}>
                <GentleReminderCard
                  label={t("sessionDetails.gentleReminderTitle")}
                  description={t("setCheckpoint.gentleReminder")}
                />
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  onClick={handleConfirm}
                  isloading={isLoading ? 1 : 0}
                >
                  {t("subjectManagement.saveChanges")}
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  style={{
                    border: "none",
                    color: "#757575",
                    fontWeight: "400",
                    marginTop: "12px",
                  }}
                  onClick={() => {
                    history.push(`/sessions/update-checkpoints/${id}`);
                  }}
                  isloading={isLoading ? 1 : 0}
                >
                  {t("subjectManagement.cancel")}
                </Button>
              </div>
            </Section>
          </PageContent>
        )}
      </div>
    </DashboardLayout>
  );
}
